import React, { useContext, useState, useEffect } from "react";
import styled from "styled-components";
import {
  Card,
  Button,
  Input,
  Select,
  Table,
  Tag,
  Statistic,
  Row,
  Col,
  message,
  Modal,
  Form,
  Divider,
  Alert,
  Tooltip,
  Badge,
} from "antd";
import {
  DollarOutlined,
  CreditCardOutlined,
  BankOutlined,
  PayCircleOutlined,
  HistoryOutlined,
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  DownloadOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../context/AuthProvider";
import {
  getTenantPayments,
  getTenantPaymentMethods,
} from "../lib/firebase/paymentModel";
import {
  createRentPaymentIntent,
  processRentPayment,
  savePaymentMethod,
  payRentPaymentACH,
} from "../lib/payments/stripe";
import {
  createPayPalOrder,
  capturePayPalPayment,
} from "../lib/payments/paypal";
import { createACHTransfer } from "../lib/payments/plaid";
import { loadStripe } from "@stripe/stripe-js";
import { device } from "../lib/breakpoints";
import { useConnectStatus } from "../hooks/useConnectStatus";

// Helper function to get auth token from Firebase
const getAuthToken = async () => {
  try {
    const { getAuth } = await import("firebase/auth");
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const token = await user.getIdToken();
    return token;
  } catch (error) {
    console.error("Error getting auth token:", error);
    throw new Error("Failed to get authentication token");
  }
};

const { Option } = Select;
const { TextArea } = Input;

const TenantPaymentPortal = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showAddMethodModal, setShowAddMethodModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentNote, setPaymentNote] = useState("");
  const [paymentForm] = Form.useForm();
  const [stripe, setStripe] = useState(null);

  // Real tenant data from Firebase
  const [tenantData, setTenantData] = useState(null);
  const [landlordStatus, setLandlordStatus] = useState(null);

  // Landlord Connect status (secure gating)
  const landlordId = tenantData?.landlordId || null;
  const {
    canAcceptPayments,
    connectStatus,
    loading: statusLoading,
    alertMessage,
    alertType,
    checkStatus,
  } = useConnectStatus(landlordId, {
    enablePolling: true,
    pollInterval: 60000,
  });

  // Prefer backend-provided status from portal API when available
  const effectiveCanAcceptPayments =
    (landlordStatus?.chargesEnabled && landlordStatus?.payoutsEnabled) ||
    canAcceptPayments;

  useEffect(() => {
    loadData();
    // Initialize Stripe
    const initStripe = async () => {
      const stripeInstance = await loadStripe(
        process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ""
      );
      setStripe(stripeInstance);
    };
    initStripe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setLoading(true);

      // Load real tenant data from Firebase
      const API_BASE_URL =
        process.env.REACT_APP_API_URL || "http://localhost:4000/api";
      const authToken = await getAuthToken();

      // Consolidated secure portal fetch (preferred)
      const portalRes = await fetch(
        `${API_BASE_URL}/tenants/${user.uid}/portal`,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      if (portalRes.ok) {
        const portal = await portalRes.json();
        if (portal?.tenant) setTenantData(portal.tenant);
        if (Array.isArray(portal?.payments)) setPayments(portal.payments);
        if (Array.isArray(portal?.paymentMethods))
          setPaymentMethods(portal.paymentMethods);
        if (portal?.landlordConnectStatus)
          setLandlordStatus(portal.landlordConnectStatus);
      } else {
        // Fallback to legacy endpoints if portal route not available
        const tenantResponse = await fetch(
          `${API_BASE_URL}/tenants/${user.uid}`,
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );
        if (tenantResponse.ok) {
          const tenant = await tenantResponse.json();
          setTenantData(tenant);
        }

        const paymentsResponse = await fetch(
          `${API_BASE_URL}/tenants/${user.uid}/payments`,
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );
        if (paymentsResponse.ok) {
          const paymentsData = await paymentsResponse.json();
          setPayments(paymentsData);
        }

        const methodsResponse = await fetch(
          `${API_BASE_URL}/tenants/${user.uid}/payment-methods`,
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );
        if (methodsResponse.ok) {
          const methodsData = await methodsResponse.json();
          setPaymentMethods(methodsData);
        }
      }
    } catch (error) {
      console.error("Error loading data:", error);
      message.error("Failed to load payment data");
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (values) => {
    try {
      setLoading(true);

      const { paymentMethod, amount, note } = values;
      const selectedMethod = paymentMethods.find((m) => m.id === paymentMethod);

      if (!selectedMethod) {
        throw new Error("Please select a payment method");
      }

      // Client-side gate: landlord must be able to accept payments (backend also validates)
      if (!effectiveCanAcceptPayments) {
        throw new Error(
          "Payments are not available for this landlord yet. Please try again later."
        );
      }

      let paymentResult;

      // Process payment based on method type
      switch (selectedMethod.type) {
        case "credit_card": {
          // Stripe payment
          const paymentIntent = await createRentPaymentIntent(
            amount * 100,
            "usd",
            {
              tenantId: user.uid,
              unitId: tenantData?.unit,
              landlordId: landlordId,
              note: note || "",
              paymentMethodId: selectedMethod.id,
            }
          );

          paymentResult = await processRentPayment(
            selectedMethod.id,
            paymentIntent.id,
            amount * 100,
            {
              tenantId: user.uid,
              unitId: tenantData?.unit,
              landlordId: landlordId,
              note: note || "",
            }
          );
          break;
        }

        case "bank_account": {
          // ACH payment - requires rentPaymentId
          // For TenantPaymentPortal, we need to find or create a rentPaymentId
          // For now, we'll try to find a pending payment from the payments list
          const pendingPayment = payments.find(
            (p) =>
              p.status === "pending" ||
              p.status === "due" ||
              p.status === "overdue"
          );

          if (!pendingPayment || !pendingPayment.id) {
            throw new Error(
              "Please pay a specific rent payment. ACH payments require a rent payment record."
            );
          }

          // Check if we have Stripe initialized
          if (!stripe) {
            throw new Error("Stripe not loaded. Please refresh the page.");
          }

          // The selectedMethod.id should be a Stripe Payment Method ID for bank accounts
          // If it's not, we'd need to collect bank account details and create one
          // For now, assume it's a valid Stripe Payment Method ID
          const bankAccountToken = selectedMethod.id;

          // Use the pending payment's ID as rentPaymentId
          const rentPaymentId = pendingPayment.id;

          // Call the backend ACH payment API
          paymentResult = await payRentPaymentACH({
            rentPaymentId: rentPaymentId,
            bankAccountToken: bankAccountToken,
          });

          break;
        }

        default:
          throw new Error("Unsupported payment method");
      }

      if (
        paymentResult.status === "succeeded" ||
        paymentResult.status === "processing"
      ) {
        message.success("Payment processed successfully!");
        setShowPaymentModal(false);
        paymentForm.resetFields();
        loadData(); // Refresh data
        checkStatus(); // Refresh landlord status just in case
      } else {
        message.error("Payment failed. Please try again.");
      }
    } catch (error) {
      console.error("Error processing payment:", error);
      message.error("Payment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getMethodIcon = (type) => {
    switch (type) {
      case "credit_card":
        return <CreditCardOutlined />;
      case "bank_account":
        return <BankOutlined />;
      default:
        return <CreditCardOutlined />;
    }
  };

  const getMethodDisplayName = (method) => {
    switch (method.type) {
      case "credit_card":
        return `${method.brand.toUpperCase()} •••• ${method.last4}`;
      case "bank_account":
        return `${method.bankName} •••• ${method.last4}`;
      default:
        return "Unknown Method";
    }
  };

  const paymentColumns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => `$${amount.toLocaleString()}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "completed" ? "success" : "processing"}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Tag>
      ),
    },
    {
      title: "Method",
      dataIndex: "method",
      key: "method",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button.Group size="small">
          <Tooltip title="View Receipt">
            <Button icon={<EyeOutlined />} />
          </Tooltip>
          <Tooltip title="Download Receipt">
            <Button icon={<DownloadOutlined />} />
          </Tooltip>
        </Button.Group>
      ),
    },
  ];

  return (
    <Container className="tenant-payment-portal-container">
      <Header>
        <Title>Payment Portal</Title>
        <Subtitle>Pay rent and manage your payment methods</Subtitle>
      </Header>

      {/* Account Overview */}
      <Row gutter={[24, 24]} style={{ marginBottom: "2rem" }}>
        <Col xs={24} md={8}>
          <Card className="section-card">
            <Statistic
              title="Monthly Rent"
              value={tenantData?.monthlyRent || 0}
              prefix={<DollarOutlined />}
              valueStyle={{ color: "#3f8600" }}
            />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card className="section-card">
            <Statistic
              title="Next Due Date"
              value={
                tenantData?.nextDueDate
                  ? new Date(tenantData.nextDueDate).toLocaleDateString()
                  : "N/A"
              }
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card className="section-card">
            <Statistic
              title="Current Balance"
              value={tenantData?.balance || 0}
              prefix={<DollarOutlined />}
              valueStyle={{
                color: (tenantData?.balance || 0) > 0 ? "#cf1322" : "#3f8600",
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Payment Methods */}
      <Card
        title="Payment Methods"
        className="section-card"
        style={{ marginBottom: "2rem" }}
      >
        <Row gutter={[16, 16]}>
          {paymentMethods.map((method) => (
            <Col xs={24} sm={12} md={8} key={method.id}>
              <PaymentMethodCard>
                <div className="method-info">
                  {getMethodIcon(method.type)}
                  <div>
                    <div className="method-name">
                      {getMethodDisplayName(method)}
                    </div>
                    {method.isDefault && (
                      <Tag color="blue" size="small">
                        Default
                      </Tag>
                    )}
                  </div>
                </div>
                <div className="method-actions">
                  <Button size="small" icon={<EyeOutlined />}>
                    View
                  </Button>
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    style={{ marginLeft: 8 }}
                  >
                    Remove
                  </Button>
                </div>
              </PaymentMethodCard>
            </Col>
          ))}
          <Col xs={24} sm={12} md={8}>
            <AddMethodCard onClick={() => setShowAddMethodModal(true)}>
              <PlusOutlined style={{ fontSize: "2rem", color: "#d9d9d9" }} />
              <div>Add Payment Method</div>
            </AddMethodCard>
          </Col>
        </Row>
      </Card>

      {/* Quick Payment */}
      <Card
        title="Quick Payment"
        className="section-card"
        style={{ marginBottom: "2rem" }}
      >
        {!effectiveCanAcceptPayments && (
          <Alert
            type={alertType || "warning"}
            message="Payments temporarily unavailable"
            description={
              alertMessage ||
              "Your landlord is still completing payment setup. Please check back soon."
            }
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        <Row gutter={[24, 24]} align="middle">
          <Col xs={24} md={12}>
            <div>
              <h4>Pay Rent for {tenantData?.unit || "Your door"}</h4>
              <p>Amount due: ${tenantData?.monthlyRent || 0}</p>
            </div>
          </Col>
          <Col xs={24} md={12} style={{ textAlign: "right" }}>
            <Button
              type="primary"
              size="large"
              icon={<PayCircleOutlined />}
              onClick={() => setShowPaymentModal(true)}
              disabled={!effectiveCanAcceptPayments || statusLoading}
            >
              Pay Now
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Payment History */}
      <Card title="Payment History" className="section-card">
        <Table
          columns={paymentColumns}
          dataSource={payments}
          loading={loading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
        />
      </Card>

      {/* Payment Modal */}
      <Modal
        title="Make Payment"
        open={showPaymentModal}
        onCancel={() => setShowPaymentModal(false)}
        footer={null}
        width={500}
      >
        <Form form={paymentForm} onFinish={handlePayment} layout="vertical">
          <Form.Item
            label="Payment Method"
            name="paymentMethod"
            rules={[
              { required: true, message: "Please select a payment method" },
            ]}
          >
            <Select placeholder="Select payment method">
              {paymentMethods.map((method) => (
                <Option key={method.id} value={method.id}>
                  {getMethodDisplayName(method)}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Amount"
            name="amount"
            rules={[{ required: true, message: "Please enter amount" }]}
          >
            <Input
              prefix={<DollarOutlined />}
              placeholder="Enter amount"
              type="number"
              step="0.01"
            />
          </Form.Item>

          <Form.Item label="Note (Optional)" name="note">
            <TextArea placeholder="Add a note about this payment" rows={3} />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              Process Payment
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Add Payment Method Modal */}
      <Modal
        title="Add Payment Method"
        open={showAddMethodModal}
        onCancel={() => setShowAddMethodModal(false)}
        footer={null}
        width={600}
      >
        <Alert
          message="Payment Method Integration"
          description="This modal would integrate with Stripe, PayPal, or Plaid to securely add payment methods. The actual implementation requires backend API endpoints and proper security measures."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <div style={{ textAlign: "center", padding: "2rem" }}>
          <p>Payment method integration requires:</p>
          <ul style={{ textAlign: "left", display: "inline-block" }}>
            <li>Backend API endpoints for secure token exchange</li>
            <li>Proper PCI compliance and security measures</li>
            <li>Integration with payment processor SDKs</li>
            <li>Webhook handling for payment status updates</li>
          </ul>
        </div>
      </Modal>
    </Container>
  );
};

export default TenantPaymentPortal;

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(
    135deg,
    var(--color-bg-primary) 0%,
    var(--color-bg-tertiary) 100%
  );
  padding: 2rem;

  @media ${device.mobile} {
    padding: 1rem;
  }

  @media ${device.tablet} {
    padding: 1.5rem;
  }

  /* Apply the new container class for better background */
  &.tenant-payment-portal-container {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  background: var(--gradient-primary);
  border-radius: 20px;
  padding: 2rem;
  color: white;
  box-shadow: 0 8px 32px rgba(44, 95, 45, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 100%
    );
    pointer-events: none;
  }
`;

const Title = styled.h1`
  font-family: var(--font-primary);
  font-size: 2.5rem;
  font-weight: 700;
  color: white;
  margin: 0 0 0.75rem 0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  letter-spacing: -0.02em;
  position: relative;
  z-index: 1;

  @media ${device.mobile} {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-family: var(--font-secondary);
  font-size: 1.125rem;
  color: white;
  margin: 0;
  opacity: 0.95;
  font-weight: 500;
  letter-spacing: -0.01em;
  position: relative;
  z-index: 1;
`;

const PaymentMethodCard = styled(Card)`
  border-radius: 12px;
  border: 2px solid var(--color-border-light);
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--color-primary);
    box-shadow: 0 4px 20px rgba(44, 95, 45, 0.1);
  }

  .method-info {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;

    .anticon {
      font-size: 1.5rem;
      color: var(--color-primary);
    }

    .method-name {
      font-weight: 600;
      color: var(--color-text);
    }
  }

  .method-actions {
    display: flex;
    justify-content: flex-end;
  }
`;

const AddMethodCard = styled(Card)`
  border-radius: 12px;
  border: 2px dashed var(--color-border-light);
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--color-primary);
    background-color: rgba(44, 95, 45, 0.02);
  }

  > div {
    color: var(--color-text-light);
    margin-top: 8px;
  }
`;
